import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useIsMobile, reveal } from "@/lib/motion";
import { PHI, GLASS, glassReveal } from "@/lib/glass";
import { PASSAGE, PASSAGE_CLOSING, PASSAGE_QUESTION } from "@/data/passage";
import { IntelligentVideo } from "@/components/living-light/LivingLight";

// Wrap key psychological trigger phrases in brighter luminance to guide the scanning eye.
function renderEmphasis(text, em) {
  if (!em || !em.length) return text;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${em.map((e) => esc(e.p)).join("|")})`, "g");
  return text.split(re).map((part, i) => {
    const hit = em.find((e) => e.p === part);
    if (!hit) return part;
    return (
      <span key={i} className={hit.gold ? "text-[#84DFF4]" : "text-white"}>
        {part}
      </span>
    );
  });
}

export default function Manifesto() {
  const mobile = useIsMobile();
  const fade = glassReveal(reveal(mobile, { duration: 1.0 }));

  return (
    <section
      id="the-passage"
      aria-labelledby="passage-heading"
      data-testid="manifesto-section"
      data-room="cool"
      className="room room--cool relative overflow-hidden px-6 sm:px-10"
      style={{ paddingTop: PHI.s6, paddingBottom: PHI.s6 }}
    >
      <div className="relative mx-auto max-w-[48rem] md:pl-14">
        <span
          aria-hidden="true"
          data-testid="passage-spine"
          className="passage-spine pointer-events-none absolute bottom-0 left-0 top-0 hidden w-px md:block"
        />

        <motion.p data-testid="manifesto-eyebrow" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} {...fade} className="type-eyebrow" style={{ marginBottom: PHI.s3 }}>The passage</motion.p>
        <motion.h2
          id="passage-heading"
          data-testid="manifesto-heading"
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          {...fade}
          className="type-headline max-w-3xl"
        >
          The Passage
        </motion.h2>

        <div
          data-testid="passage-prose"
          className="type-prose max-w-[66ch] space-y-7 text-pretty sm:space-y-8"
          style={{ marginTop: PHI.s4 }}
        >
          {PASSAGE.map(({ id, text, em }) => (
            <motion.p
              key={id}
              data-testid={`passage-${id}`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              {...fade}
              className={id === "turn" ? "type-voice my-16 !text-[clamp(2rem,1.4rem+2vw,3rem)] text-[#84DFF4] sm:my-24" : undefined}
            >
              {renderEmphasis(text, em)}
            </motion.p>
          ))}

          <div data-testid="passage-closing" style={{ paddingTop: PHI.s4 }}>
            <motion.p
              data-testid="passage-closing-line"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.2, ease: GLASS.settle }}
              className="type-voice text-pretty !text-[clamp(1.25rem,1rem+0.8vw,1.6rem)] text-[#F5F5F7]/70"
            >
              {PASSAGE_CLOSING}
            </motion.p>

            <motion.span
              aria-hidden="true"
              data-testid="passage-closing-hairline"
              initial={{ opacity: 0, scaleX: 0.15 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.5, delay: 0.3, ease: GLASS.settle }}
              className="gold-hairline origin-left"
              style={{ marginTop: PHI.s3, marginBottom: PHI.s3 }}
            />

            <motion.p
              data-testid="passage-cta"
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.4, delay: 0.5, ease: GLASS.bloom }}
              onViewportEnter={() => window.dispatchEvent(new CustomEvent("prism:held-breath"))}
              className="passage-cta type-voice !text-[clamp(2rem,1.4rem+2.2vw,3.25rem)]"
            >
              {PASSAGE_QUESTION}
            </motion.p>
          </div>

          <motion.div
            data-testid="passage-video"
            initial={{ opacity: 0, scale: 0.94, y: 28, filter: "blur(12px)" }}
            whileInView={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 2.4, ease: GLASS.settle }}
            className="passage-portal relative"
            style={{ marginTop: PHI.s4 }}
          >
            <span aria-hidden="true" className="passage-portal-aura" />
            <IntelligentVideo
              portrait
              ethereal
              videoUrl="https://player.mediadelivery.net/embed/754316/941d5e41-f449-4036-a7c7-65a4ce3c6e4b?autoplay=true&loop=true&muted=true&preload=true&responsive=true&compactControls=true&showSpeed=false&chromecast=false&disableAirPlay=true&rememberPosition=false"
            />
            <motion.p
              data-testid="passage-video-whisper"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.6, delay: 1.2, ease: GLASS.settle }}
              className="type-record -mt-6 text-center text-[#8E8E93]/50 md:-mt-8"
            >
              A short film, offered quietly
            </motion.p>
          </motion.div>

          <motion.div
            data-testid="passage-conclusion"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            {...fade}
            className="pt-16 sm:pt-24"
          >
            <p
              data-testid="passage-conclusion-copy"
              className="type-prose mt-4 max-w-[66ch] text-pretty sm:mt-6"
            >
              We have spent years in the quiet pursuit of this light, and we are deeply honored to finally place it into your hands. This is not merely a material; it is <span className="text-white">a canvas for your most profound work</span>—a quiet reward for those brave enough to redefine the possible. <span className="text-white">The pathways have already been charted for you.</span>
            </p>
            <Link
              data-testid="passage-dossier-link"
              to="/threshold/archive/glass"
              className="mt-8 inline-block border-b border-[#84DFF4]/30 pb-px font-mono-spec text-[0.6rem] tracking-[0.24em] text-[#84DFF4]/85 transition-colors duration-300 hover:border-[#84DFF4] hover:text-[#84DFF4]"
            >
              The master dossier · one hundred use cases, the optical physics, side-by-side comparisons →
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}